import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { StoreProvider } from "@/contexts/StoreContext";
import Header from "@/components/Header";
import SideMenu from "@/components/SideMenu";
import Home from "@/components/Home";
import StorePage from "@/components/StorePage";
import CategoryPage from "@/components/CategoryPage";
import Login from "@/components/Login";
import Register from "@/components/Register";
import Cart from "@/components/Cart";
import Checkout from "@/components/Checkout";
import OrderHistory from "@/components/OrderHistory";
import OrderTracking from "@/components/OrderTracking";
import Wishlist from "@/components/Wishlist";
import AdminDashboard from "@/components/AdminDashboard";
import AdminProducts from "@/components/AdminProducts";
import AdminOrders from "@/components/AdminOrders";
import AdminFulfillment from "@/components/AdminFulfillment";
import AdminStoreAdmins from "@/components/AdminStoreAdmins";
import AdminSettings from "@/components/AdminSettings";
import MerchantKit from "@/components/MerchantKit";
import AdminLeads from "@/components/AdminLeads";
import AdminKitAnalytics from "@/components/AdminKitAnalytics";
import AdminCoupons from "@/components/AdminCoupons";
import AdminBranches from "@/components/AdminBranches";
import AdminInvites from "@/components/AdminInvites";
import AdminBilling from "@/components/AdminBilling";
import AdminPromos from "@/components/AdminPromos";
import AcceptInvite from "@/components/AcceptInvite";
import PaymentCallback from "@/components/PaymentCallback";
import Subscriptions from "@/components/Subscriptions";
import InstallPrompt from "@/components/InstallPrompt";

// Register Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then(reg => console.log('SW registered:', reg.scope))
      .catch(err => console.log('SW registration failed:', err));
  });
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen">Loading...</div>;
  if (!user || user === false) return <Navigate to="/login" />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen">Loading...</div>;
  if (!user || user === false) return <Navigate to="/login" />;
  if (user.role !== "admin" && user.role !== "store_admin") return <Navigate to="/" />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen">Loading...</div>;
  if (!user || user === false) return <Navigate to="/login" />;
  if (user.role !== "admin") return <Navigate to="/admin" />;
  return children;
}

function AppContent() {
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <div className="app-container" data-testid="app-container">
      <Header onMenuToggle={() => setMenuOpen(!menuOpen)} />
      <SideMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
      <main className="main-content">
        <InstallPrompt />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/store/:slug" element={<StorePage />} />
          <Route path="/category/:slug" element={<CategoryPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><OrderHistory /></ProtectedRoute>} />
          <Route path="/track/:orderId" element={<ProtectedRoute><OrderTracking /></ProtectedRoute>} />
          <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
          <Route path="/subscriptions" element={<ProtectedRoute><Subscriptions /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
          <Route path="/admin/orders" element={<AdminRoute><AdminOrders /></AdminRoute>} />
          <Route path="/admin/fulfillment" element={<AdminRoute><AdminFulfillment /></AdminRoute>} />
          <Route path="/admin/store-admins" element={<SuperAdminRoute><AdminStoreAdmins /></SuperAdminRoute>} />
          <Route path="/admin/settings" element={<SuperAdminRoute><AdminSettings /></SuperAdminRoute>} />
          <Route path="/merchant-kit" element={<MerchantKit />} />
          <Route path="/merchant-kit/:slug" element={<MerchantKit />} />
          <Route path="/admin/leads" element={<SuperAdminRoute><AdminLeads /></SuperAdminRoute>} />
          <Route path="/admin/kit-analytics" element={<SuperAdminRoute><AdminKitAnalytics /></SuperAdminRoute>} />
          <Route path="/admin/coupons" element={<SuperAdminRoute><AdminCoupons /></SuperAdminRoute>} />
          <Route path="/admin/branches" element={<SuperAdminRoute><AdminBranches /></SuperAdminRoute>} />
          <Route path="/admin/invites" element={<SuperAdminRoute><AdminInvites /></SuperAdminRoute>} />
          <Route path="/admin/billing" element={<SuperAdminRoute><AdminBilling /></SuperAdminRoute>} />
          <Route path="/admin/promos" element={<AdminRoute><AdminPromos /></AdminRoute>} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/payment-callback" element={<PaymentCallback />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <StoreProvider>
              <AppContent />
            </StoreProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
